//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import styled from "@emotion/styled";
import _ from "lodash";
import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Dropdown, Form, Icon, Loader, Menu, Message } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  CreditPlan as ApiCreditPlan,
  EstimateDeploymentUsageRequest as ApiEstimateDeploymentUsageRequest,
  EstimatedDeploymentUsage as ApiEstimatedDeploymentUsage,
  Money as ApiMoney,
  SupportPlanPrice as ApiSupportPlanPrice,
} from "../../api/lib";
import { Routes } from "../../routes";
import {
  ContentSegment,
  DiskPerformanceInput,
  ErrorMessage,
  LoaderBox,
  MenuActionBack,
  ModelInput,
  NodeSizeInput,
  ProviderInput,
  RegionInput,
  SecondaryMenu,
} from "../../ui/lib";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";

// ArangoDB Platform bundles. An empty bundle and "coredb" mean the platform is off, so the
// deployment has no gateway pods; the others also raise the AEU type ratio. CoreDB uses the
// explicit "coredb" value, because the dropdown shows an empty value as no selection.
const platformBundleOptions = [
  { key: "coredb", value: "coredb", text: "CoreDB (no platform)" },
  { key: "aisuite", value: "aisuite", text: "AI Suite" },
  { key: "datascience", value: "datascience", text: "Data Science" },
];

// Network transfer destinations that can be assumed. Unlike the compute components,
// network usage does not follow from the configuration and is entered by hand.
const networkDestinations = ["Internet", "InCluster", "PrivateEndpoint"];

// Common period presets, so the usual "per month" question needs no arithmetic.
const hoursPresets = [
  { value: 1, text: "1 hour" },
  { value: 24, text: "1 day" },
  { value: 730, text: "1 month" },
  { value: 8760, text: "1 year" },
];

// Label of a period: the preset name when it matches one, otherwise the plain hours.
const periodLabel = (hours: number): string => {
  const preset = _.find(hoursPresets, (p) => p.value === hours);
  return preset ? preset.text : `${formatNumber(hours, 0)} h`;
};

// Delay between the last change and the estimate request, so dragging a slider does not
// send a request per pixel.
const estimateDebounceMs = 350;

// Render a Money value as a number, consistent with the credit/usage views.
const moneyAsNumber = (m?: ApiMoney): number => {
  if (!m) {
    return 0;
  }
  return Number(m.units || 0) + (m.nanos || 0) / 1e9;
};

// Format a number with the given decimals and a space between every three digits of the
// integer part, e.g. 109979.942 as "109 979.94". The space is non-breaking so that a value
// never wraps inside a table cell.
const formatNumber = (value: number, decimals: number = 2): string => {
  const [integer, fraction] = value.toFixed(decimals).split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction ? `${grouped}.${fraction}` : grouped;
};

// Format a quantity like the costs, but keep enough precision below one to see the sidecar
// contribution.
const formatQuantity = (q?: number): string => {
  const value = q || 0;
  if (value === 0) {
    return "0";
  }
  return formatNumber(value, Math.abs(value) < 1 ? 6 : 2);
};

// The support plan factor of a credit plan row. The factor is a float in the API, so it may
// arrive as a number or a string.
const supportFactor = (spp?: ApiSupportPlanPrice): number => {
  const factor = Number((spp && spp.factor) || 0);
  return factor > 0 ? factor : 1;
};

// Label of an estimated item, disambiguating the components that appear more than once or
// that carry a tier.
const itemLabel = (item: { kind?: string; network_transfer_destination?: string; disk_performance_id?: string; addon_id?: string }): string => {
  if (item.addon_id) {
    return `${item.kind} (${item.addon_id})`;
  }
  if (item.network_transfer_destination) {
    return `${item.kind} (${item.network_transfer_destination})`;
  }
  if (item.disk_performance_id) {
    return `${item.kind} (${item.disk_performance_id})`;
  }
  return item.kind || "";
};

// The only usage kind the AEU discount applies to. The other revenue kinds of a plan
// (Dedicated DC) get the support factor but no discount.
const aeuKind = "DeploymentAEU";

// Colors of the AEU, Dedicated DC and infrastructure parts, on the dark total card and on white.
const revenueColor = "#ffd166";
const revenueColorOnWhite = "#f2a900";
const ddcColor = "#7ee0b5";
const ddcColorOnWhite = "#21a66b";
const infraColor = "rgba(255,255,255,0.7)";
const infraColorOnWhite = "#2185d0";

// The pricing part of a usage kind under a plan with revenue_usage_kinds.
type PricingPart = "aeu" | "ddc" | "infra";
const pricingPart = (kind: string | undefined, revenueKinds: string[]): PricingPart => {
  if (!_.includes(revenueKinds, kind || "")) {
    return "infra";
  }
  return kind === aeuKind ? "aeu" : "ddc";
};

// Sum of the amounts of the priced items that belong to the given pricing part.
const sumAmount = (result: ApiEstimatedDeploymentUsage | undefined, revenueKinds: string[], part: PricingPart): number =>
  _.sumBy(
    _.filter(result?.items || [], (item) => !!item.has_rate && pricingPart(item.kind, revenueKinds) === part),
    (item) => moneyAsNumber(item.amount)
  );

const Page = styled("div")`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 24px;
  align-items: start;
  padding: 8px 0 24px 0;
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Column = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled("div")`
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const CardTitle = styled("div")`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldGrid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px 24px;
`;

const FieldLabel = styled("div")`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const Hint = styled("div")`
  font-size: 12px;
  color: #6b7280;
  margin-top: 12px;
  line-height: 1.5;
`;

const SliderRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  input[type="range"] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 3px;
    outline: none;
    cursor: pointer;
  }
  input[type="range"]:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  input[type="range"]:focus-visible {
    outline: 2px solid #2185d0;
    outline-offset: 4px;
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #2185d0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #2185d0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ValueBox = styled("div")`
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 8px;
  background: #fff;
  input {
    width: 72px;
    border: none;
    outline: none;
    text-align: right;
    padding: 6px 4px;
    font-variant-numeric: tabular-nums;
    background: transparent;
  }
  span {
    color: #6b7280;
    font-size: 12px;
    white-space: nowrap;
  }
  &:focus-within {
    border-color: #2185d0;
    box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.25);
  }
`;

const Chips = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Chip = styled("button")<{ active: boolean }>`
  border: 1px solid ${(p) => (p.active ? "#2185d0" : "#d1d5db")};
  background: ${(p) => (p.active ? "#eaf3fb" : "#fff")};
  color: ${(p) => (p.active ? "#1a69a4" : "#374151")};
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
`;

const Summary = styled("div")`
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TotalCard = styled(Card)`
  background: linear-gradient(135deg, #1f3b57 0%, #2185d0 100%);
  color: #fff;
  border: none;
`;

const TotalLabel = styled("div")`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.8;
`;

const TotalValue = styled("div")`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
  margin: 4px 0;
  font-variant-numeric: tabular-nums;
`;

const TotalSub = styled("div")`
  font-size: 13px;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
`;

const SplitBar = styled("div")`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.25);
  margin-top: 16px;
`;

const SplitRow = styled("div")`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 6px;
  }
`;

// What a summary row is subject to (support factor, AEU discount), under its label.
const SplitNote = styled("div")`
  font-size: 11px;
  opacity: 0.75;
  margin: 1px 0 0 14px;
`;

const SplitSubRow = styled(SplitRow)`
  margin-top: 2px;
  padding-left: 14px;
  opacity: 0.9;
`;

const Formula = styled("div")`
  font-size: 12px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
`;

const GroupTag = styled("small")`
  margin-left: 8px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`;

const BreakdownGroup = styled("div")`
  & + & {
    margin-top: 20px;
  }
`;

const GroupHeader = styled("div")<{ color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  padding-bottom: 6px;
  margin-bottom: 4px;
  border-bottom: 2px solid ${(p) => p.color};
  span:last-of-type {
    color: #1f2937;
    font-variant-numeric: tabular-nums;
  }
`;

const ItemRow = styled("div")<{ muted: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2px 12px;
  padding: 8px 6px;
  border-radius: 8px;
  opacity: ${(p) => (p.muted ? 0.5 : 1)};
  &:hover {
    background: #f6f8fa;
  }
`;

const ItemName = styled("div")`
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemAmount = styled("div")`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const ItemQuantity = styled("div")`
  font-size: 12px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
`;

const ItemShare = styled("div")`
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const ShareBar = styled("div")`
  grid-column: 1 / -1;
  height: 4px;
  border-radius: 2px;
  background: #eef0f3;
  overflow: hidden;
  margin-top: 4px;
  div {
    height: 100%;
    border-radius: 2px;
  }
`;

const NoRate = styled("span")`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  background: #eef0f3;
  border-radius: 999px;
  padding: 1px 8px;
`;

const SizeChips = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
  span {
    font-size: 12px;
    color: #374151;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 3px 8px;
    font-variant-numeric: tabular-nums;
  }
`;

const Placeholder = styled("div")`
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  padding: 24px 8px;
`;

interface ISliderFieldProps {
  label: string;
  value: number;
  min: number;
  // Upper end of the slider; the number box accepts values up to inputMax.
  max: number;
  inputMax?: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}

interface ISliderFieldState {
  text: string;
  prevValue: number;
}

// A slider with a number box next to it. The slider changes the value while dragging; the
// box applies on blur or Enter and accepts values beyond the slider range (inputMax).
class SliderField extends Component<ISliderFieldProps, ISliderFieldState> {
  state: ISliderFieldState = { text: `${this.props.value}`, prevValue: this.props.value };

  static getDerivedStateFromProps(props: ISliderFieldProps, state: ISliderFieldState) {
    if (props.value !== state.prevValue) {
      return { text: `${props.value}`, prevValue: props.value };
    }
    return null;
  }

  clamp = (value: number) => Math.min(Math.max(value, this.props.min), this.props.inputMax || this.props.max);

  commitText = () => {
    const parsed = Number(this.state.text.replace(",", "."));
    if (isNaN(parsed)) {
      this.setState({ text: `${this.props.value}` });
      return;
    }
    // Whole-step fields (node counts) take whole numbers; fractional ones (AEU discount) keep the fraction.
    const value = this.clamp(Number.isInteger(this.props.step || 1) ? Math.round(parsed) : parsed);
    this.setState({ text: `${value}` });
    if (value !== this.props.value) {
      this.props.onChange(value);
    }
  };

  render() {
    const { label, value, min, max, step, unit, disabled } = this.props;
    const pct = max > min ? ((Math.min(value, max) - min) / (max - min)) * 100 : 0;
    return (
      <div>
        <FieldLabel>{label}</FieldLabel>
        <SliderRow>
          <input
            type="range"
            min={min}
            max={max}
            step={step || 1}
            value={Math.min(value, max)}
            disabled={disabled}
            aria-label={label}
            style={{ background: `linear-gradient(to right, #2185d0 ${pct}%, #e5e7eb ${pct}%)` }}
            onChange={(e) => this.props.onChange(Number(e.target.value))}
          />
          <ValueBox>
            <input
              value={this.state.text}
              disabled={disabled}
              aria-label={label}
              onChange={(e) => this.setState({ text: e.target.value })}
              onBlur={this.commitText}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  this.commitText();
                }
              }}
            />
            {unit && <span>{unit}</span>}
          </ValueBox>
        </SliderRow>
      </div>
    );
  }
}

interface INetworkUsageState {
  [destination: string]: number;
}

// Properties of the credit plan calculator page.
interface ICreditPlanCalculatorProps extends IWithRefreshProps, RouteComponentProps {
  // The id of the credit plan the estimate is priced against (not necessarily the active one).
  planId: string;
  loading: boolean;
}

// State of the credit plan calculator page.
interface ICreditPlanCalculatorState {
  plan?: ApiCreditPlan;
  calculating: boolean;
  provider_id: string;
  region_id: string;
  model: string;
  node_size_id: string;
  node_count: number;
  node_disk_size: number;
  coordinators: number;
  coordinator_memory_size: number;
  dbservers: number;
  dbserver_memory_size: number;
  dbserver_disk_size: number;
  disk_performance_id: string;
  support_plan_id: string;
  aeu_discount_percent: number;
  dedicated_data_cluster: boolean;
  arangodb_platform_bundle: string;
  hours: number;
  network_usage: INetworkUsageState;
  cloud_storage_gib: number;
  gpu_hours: number;
  result?: ApiEstimatedDeploymentUsage;
  // The same configuration without the AEU discount, to show the list price next to it.
  listResult?: ApiEstimatedDeploymentUsage;
  errorMessage?: string;
}

// The inputs of the estimate; a change of any of them triggers a new estimate.
const estimateInputs: (keyof ICreditPlanCalculatorState)[] = [
  "provider_id",
  "region_id",
  "model",
  "node_size_id",
  "node_count",
  "node_disk_size",
  "coordinators",
  "coordinator_memory_size",
  "dbservers",
  "dbserver_memory_size",
  "dbserver_disk_size",
  "disk_performance_id",
  "support_plan_id",
  "aeu_discount_percent",
  "dedicated_data_cluster",
  "arangodb_platform_bundle",
  "hours",
  "network_usage",
  "cloud_storage_gib",
  "gpu_hours",
];

// Full-page calculator that estimates the Billing 2.0 usage of a deployment configuration
// and prices it live against the given credit plan. Read-only / calculation only.
class CreditPlanCalculator extends Component<ICreditPlanCalculatorProps, ICreditPlanCalculatorState> {
  state: ICreditPlanCalculatorState = {
    plan: undefined,
    calculating: false,
    // Most estimates are for AWS us-east-1, so start there to save a few clicks.
    provider_id: "aws",
    region_id: "aws-us-east-1",
    model: "sharded",
    node_size_id: "",
    node_count: 3,
    node_disk_size: 512,
    coordinators: 3,
    coordinator_memory_size: 4,
    dbservers: 3,
    dbserver_memory_size: 12,
    dbserver_disk_size: 512,
    disk_performance_id: "",
    support_plan_id: "",
    aeu_discount_percent: 0,
    dedicated_data_cluster: false,
    arangodb_platform_bundle: "coredb",
    hours: 730,
    network_usage: {},
    cloud_storage_gib: 0,
    gpu_hours: 0,
    result: undefined,
    listResult: undefined,
    errorMessage: undefined,
  };

  // Pending debounce timer and the sequence number of the latest request; responses of
  // older requests are dropped so a slow answer never overwrites a newer one.
  timer?: ReturnType<typeof setTimeout>;
  requestSeq = 0;

  reloadPlan = async () => {
    const plan = await apiClients.idashboardClient.GetCreditPlan({ id: this.props.planId });
    // Start on the plan's "default" support plan (factor 1) when it has one.
    const support_plan_id = this.state.support_plan_id || (_.some(plan.support_plan_prices, (spp) => spp.id === "default") ? "default" : "");
    this.setState({ plan: plan, support_plan_id: support_plan_id });
  };

  componentDidMount() {
    this.props.refreshNow && this.props.refreshNow(this.reloadPlan);
  }

  componentDidUpdate(prevProps: ICreditPlanCalculatorProps, prevState: ICreditPlanCalculatorState) {
    if (_.some(estimateInputs, (key) => !_.isEqual(this.state[key], prevState[key]))) {
      this.scheduleEstimate();
    }
  }

  componentWillUnmount() {
    this.timer && clearTimeout(this.timer);
    // Invalidate in-flight requests.
    this.requestSeq++;
  }

  isFlexible = () => this.state.model === "flexible" || this.state.model === "";

  // Reason why no estimate can be made yet, or empty when the inputs are complete.
  missingInput = (): string => {
    if (!this.state.region_id) {
      return "Select a cloud provider and region to see the estimate.";
    }
    if (!this.isFlexible() && !this.state.node_size_id) {
      return "Select a node size to see the estimate.";
    }
    return "";
  };

  scheduleEstimate = () => {
    this.timer && clearTimeout(this.timer);
    // Invalidate in-flight requests right away, so an answer arriving during the debounce is
    // not shown for the new inputs.
    this.requestSeq++;
    if (this.missingInput()) {
      this.setState({ calculating: false, result: undefined, listResult: undefined, errorMessage: undefined });
      return;
    }
    this.timer = setTimeout(this.estimate, estimateDebounceMs);
  };

  buildRequest = (aeu_discount_percent: number): ApiEstimateDeploymentUsageRequest => ({
    // Price against the plan version being viewed rather than the active one.
    credit_plan_id: this.props.planId,
    cloud_provider_id: this.state.provider_id,
    cloud_region_id: this.state.region_id,
    model: this.state.model,
    node_size_id: this.state.node_size_id,
    node_count: this.state.node_count,
    node_disk_size: this.state.node_disk_size,
    coordinators: this.state.coordinators,
    coordinator_memory_size: this.state.coordinator_memory_size,
    dbservers: this.state.dbservers,
    dbserver_memory_size: this.state.dbserver_memory_size,
    dbserver_disk_size: this.state.dbserver_disk_size,
    disk_performance_id: this.state.disk_performance_id,
    support_plan_id: this.state.support_plan_id,
    arangodb_platform_bundle: this.state.arangodb_platform_bundle,
    hours: this.state.hours,
    cloud_storage_gib: this.state.cloud_storage_gib,
    gpu_hours: this.state.gpu_hours,
    dedicated_data_cluster: this.state.dedicated_data_cluster,
    aeu_discount_percent: aeu_discount_percent,
    // Only send destinations the user actually put a number against.
    network_usage: _.map(
      _.filter(_.keys(this.state.network_usage), (destination) => !!this.state.network_usage[destination]),
      (destination) => ({ destination: destination, gib: this.state.network_usage[destination] })
    ),
  });

  estimate = async () => {
    const seq = this.requestSeq;
    const discount = this.state.aeu_discount_percent;
    this.setState({ calculating: true });
    try {
      const [result, listResult] = await Promise.all([
        apiClients.idashboardClient.EstimateDeploymentUsage(this.buildRequest(discount)),
        discount > 0 ? apiClients.idashboardClient.EstimateDeploymentUsage(this.buildRequest(0)) : Promise.resolve(undefined),
      ]);
      if (seq !== this.requestSeq) {
        return;
      }
      this.setState({ calculating: false, result: result, listResult: listResult, errorMessage: undefined });
    } catch (e) {
      if (seq !== this.requestSeq) {
        return;
      }
      this.setState({ calculating: false, result: undefined, listResult: undefined, errorMessage: `${e}` });
    }
  };

  onProviderChange = (id: string) => this.setState({ provider_id: id, region_id: "", node_size_id: "", disk_performance_id: "" });
  onRegionChange = (id: string) => this.setState({ region_id: id, node_size_id: "", disk_performance_id: "" });
  onModelChange = (value: string) => {
    // A one-shard deployment is always three nodes, a developer or free one always one.
    const node_count = value === "oneshard" ? 3 : value === "developer" || value === "free" ? 1 : this.state.node_count;
    this.setState({ model: value, node_count: node_count });
  };
  onNodeSizeChange = (id: string) => this.setState({ node_size_id: id, disk_performance_id: "" });

  renderDeployment() {
    const is_flexible = this.isFlexible();
    const is_oneshard = this.state.model === "oneshard";
    const is_single = this.state.model === "developer" || this.state.model === "free";
    return (
      <Card>
        <CardTitle>
          <Icon name="server" /> Deployment
        </CardTitle>
        <Form>
          <FieldGrid>
            <Form.Field>
              <FieldLabel>Cloud provider</FieldLabel>
              <ProviderInput {...this.props} placeholder="cloud provider" id={this.state.provider_id} onChange={this.onProviderChange} />
            </Form.Field>
            <Form.Field>
              <FieldLabel>Region</FieldLabel>
              <RegionInput
                {...this.props}
                placeholder="cloud region"
                id={this.state.region_id}
                providerId={this.state.provider_id}
                onChange={this.onRegionChange}
              />
            </Form.Field>
            <Form.Field>
              <FieldLabel>Model</FieldLabel>
              <ModelInput {...this.props} placeholder="model" id={this.state.model} onChange={this.onModelChange} />
            </Form.Field>
            <Form.Field>
              <FieldLabel>Platform bundle</FieldLabel>
              <Dropdown
                fluid
                selection
                options={platformBundleOptions}
                value={this.state.arangodb_platform_bundle}
                onChange={(e, d) => this.setState({ arangodb_platform_bundle: d.value as string })}
              />
            </Form.Field>
            {!is_flexible && (
              <Form.Field>
                <FieldLabel>Node size</FieldLabel>
                <NodeSizeInput
                  {...this.props}
                  placeholder="node size"
                  id={this.state.node_size_id}
                  region_id={this.state.region_id}
                  model={this.state.model}
                  onChange={this.onNodeSizeChange}
                />
              </Form.Field>
            )}
            <Form.Field>
              <FieldLabel>Disk performance</FieldLabel>
              <DiskPerformanceInput
                {...this.props}
                placeholder="disk performance"
                id={this.state.disk_performance_id}
                onChange={(id: string) => this.setState({ disk_performance_id: id })}
                notAll
                regionId={this.state.region_id}
                nodeSizeId={this.state.node_size_id}
                nodeDiskSize={is_flexible ? this.state.dbserver_disk_size : this.state.node_disk_size}
              />
            </Form.Field>
          </FieldGrid>
        </Form>
        <div style={{ marginTop: 20 }}>
          <FieldGrid>
            {!is_flexible && (
              <SliderField
                label="Node count"
                min={is_single ? 1 : 3}
                max={is_single ? 1 : is_oneshard ? 3 : 20}
                disabled={is_oneshard || is_single}
                value={this.state.node_count}
                onChange={(v) => this.setState({ node_count: v })}
              />
            )}
            {!is_flexible && (
              <SliderField
                label="Node disk size"
                min={8}
                max={16384}
                inputMax={4194304}
                step={8}
                unit="GiB"
                value={this.state.node_disk_size}
                onChange={(v) => this.setState({ node_disk_size: v })}
              />
            )}
            {is_flexible && (
              <SliderField label="Coordinators" min={0} max={64} value={this.state.coordinators} onChange={(v) => this.setState({ coordinators: v })} />
            )}
            {is_flexible && (
              <SliderField
                label="Coordinator memory"
                min={0}
                max={256}
                inputMax={1024}
                unit="GiB"
                value={this.state.coordinator_memory_size}
                onChange={(v) => this.setState({ coordinator_memory_size: v })}
              />
            )}
            {is_flexible && <SliderField label="DB servers" min={0} max={64} value={this.state.dbservers} onChange={(v) => this.setState({ dbservers: v })} />}
            {is_flexible && (
              <SliderField
                label="DB server memory"
                min={0}
                max={256}
                inputMax={1024}
                unit="GiB"
                value={this.state.dbserver_memory_size}
                onChange={(v) => this.setState({ dbserver_memory_size: v })}
              />
            )}
            {is_flexible && (
              <SliderField
                label="DB server disk size"
                min={8}
                max={16384}
                inputMax={4194304}
                step={8}
                unit="GiB"
                value={this.state.dbserver_disk_size}
                onChange={(v) => this.setState({ dbserver_disk_size: v })}
              />
            )}
          </FieldGrid>
        </div>
      </Card>
    );
  }

  renderSupport() {
    const plan = this.state.plan;
    const supportPlanPrices = (plan && plan.support_plan_prices) || [];
    const revenueKinds = (plan && plan.revenue_usage_kinds) || [];
    const selected = _.find(supportPlanPrices, (spp) => spp.id === this.state.support_plan_id);
    return (
      <Card>
        <CardTitle>
          <Icon name="handshake outline" /> Support & discount
        </CardTitle>
        <FieldLabel>Support plan</FieldLabel>
        <Chips>
          {_.map(supportPlanPrices, (spp) => (
            <Chip key={spp.id} active={spp.id === this.state.support_plan_id} onClick={() => this.setState({ support_plan_id: spp.id || "" })}>
              {spp.id} ×{formatNumber(supportFactor(spp))}
            </Chip>
          ))}
        </Chips>
        <div style={{ marginTop: 20 }}>
          <SliderField
            label="AEU discount"
            min={0}
            max={100}
            step={0.5}
            unit="%"
            disabled={_.isEmpty(revenueKinds)}
            value={this.state.aeu_discount_percent}
            onChange={(v) => this.setState({ aeu_discount_percent: v })}
          />
        </div>
        <Hint>
          {_.isEmpty(revenueKinds)
            ? `This credit plan multiplies every component by the support factor${
                selected ? ` (×${formatNumber(supportFactor(selected))})` : ""
              } and applies no AEU discount.`
            : `AEU is reduced by the AEU discount and then multiplied by the support factor${
                selected ? ` (×${formatNumber(supportFactor(selected))})` : ""
              }. Dedicated DC gets the support factor only, without the discount. Infrastructure is priced at its plain rate.`}
        </Hint>
        <div style={{ marginTop: 16 }}>
          <Form>
            <Form.Checkbox
              toggle
              label="Dedicated data cluster"
              checked={this.state.dedicated_data_cluster}
              onChange={(e, d) => this.setState({ dedicated_data_cluster: !!d.checked })}
            />
          </Form>
          {this.state.dedicated_data_cluster && (
            <Hint>
              The dedicated data cluster fee is charged once per data cluster, not per deployment: further deployments of the customer in the same region run on
              the same cluster without paying it again. A deployment in another region needs a new data cluster, so the fee is charged again for it.
            </Hint>
          )}
        </div>
      </Card>
    );
  }

  renderUsage() {
    return (
      <Card>
        <CardTitle>
          <Icon name="clock outline" /> Usage assumptions
        </CardTitle>
        <SliderField label="Period" min={1} max={8760} inputMax={87600} unit="h" value={this.state.hours} onChange={(v) => this.setState({ hours: v })} />
        <Chips>
          {_.map(hoursPresets, (p) => (
            <Chip key={p.value} active={this.state.hours === p.value} onClick={() => this.setState({ hours: p.value })}>
              {p.text}
            </Chip>
          ))}
        </Chips>
        <div style={{ marginTop: 20 }}>
          <FieldGrid>
            {_.map(networkDestinations, (destination) => (
              <SliderField
                key={destination}
                label={`Network ${destination}`}
                min={0}
                max={10000}
                inputMax={10000000}
                unit="GiB"
                value={this.state.network_usage[destination] || 0}
                onChange={(v) => this.setState({ network_usage: { ...this.state.network_usage, [destination]: v } })}
              />
            ))}
            <SliderField
              label="Backup size"
              min={0}
              max={10000}
              inputMax={10000000}
              unit="GiB"
              value={this.state.cloud_storage_gib}
              onChange={(v) => this.setState({ cloud_storage_gib: v })}
            />
            <SliderField
              label="GPU usage"
              min={0}
              max={8760}
              inputMax={10000000}
              unit="GPU-h"
              value={this.state.gpu_hours}
              onChange={(v) => this.setState({ gpu_hours: v })}
            />
          </FieldGrid>
        </div>
        <Hint>
          Backup size is held for the whole period and scales with it. Network transfer and GPU usage are totals for the period and stay as entered, so a GPU
          job that runs for only part of the period can be expressed.
        </Hint>
      </Card>
    );
  }

  // The support factor of the selected support plan.
  selectedSupportFactor(): number {
    const plan = this.state.plan;
    return supportFactor(_.find((plan && plan.support_plan_prices) || [], (spp) => spp.id === this.state.support_plan_id));
  }

  // What each pricing part is subject to, e.g. "support ×1.15 · discount −20%".
  partNote(part: PricingPart): string {
    const factor = `support ×${formatNumber(this.selectedSupportFactor())}`;
    const discount = this.state.aeu_discount_percent;
    switch (part) {
      case "aeu":
        return discount > 0 ? `${factor} · discount −${formatNumber(discount)}%` : `${factor} · no discount`;
      case "ddc":
        return `${factor} · no discount`;
      default:
        return "plain rate · no support, no discount";
    }
  }

  // The estimated items grouped into AEU, Dedicated DC and infrastructure (one unnamed group for
  // a legacy plan), each sorted by amount so the biggest cost comes first.
  breakdownGroups() {
    const { result, plan } = this.state;
    const revenueKinds = (plan && plan.revenue_usage_kinds) || [];
    const items = _.orderBy(result?.items || [], [(item) => !!item.has_rate, (item) => moneyAsNumber(item.amount)], ["desc", "desc"]);
    const group = (name: string, color: string, groupItems: typeof items, tag = "") => ({
      name: name,
      tag: tag,
      color: color,
      items: groupItems,
      amount: _.sumBy(
        _.filter(groupItems, (item) => !!item.has_rate),
        (item) => moneyAsNumber(item.amount)
      ),
    });
    if (_.isEmpty(revenueKinds)) {
      return [group("", infraColorOnWhite, items)];
    }
    const byPart = _.groupBy(items, (item) => pricingPart(item.kind, revenueKinds));
    return _.filter(
      [
        group("AEU", revenueColorOnWhite, byPart.aeu || [], this.partNote("aeu")),
        group("Dedicated DC", ddcColorOnWhite, byPart.ddc || [], this.partNote("ddc")),
        group("Infrastructure", infraColorOnWhite, byPart.infra || [], this.partNote("infra")),
      ],
      (g) => !_.isEmpty(g.items)
    );
  }

  renderSummary() {
    const { result, listResult, calculating, plan } = this.state;
    const revenueKinds = (plan && plan.revenue_usage_kinds) || [];
    const missing = this.missingInput();
    const total = moneyAsNumber(result && result.total_amount);
    const listTotal = listResult ? moneyAsNumber(listResult.total_amount) : total;
    const aeu = sumAmount(result, revenueKinds, "aeu");
    // AEU before the discount; the difference is shown as its own row.
    const listAeu = listResult ? sumAmount(listResult, revenueKinds, "aeu") : aeu;
    const discountAmount = listAeu - aeu;
    const ddc = sumAmount(result, revenueKinds, "ddc");
    const infra = sumAmount(result, revenueKinds, "infra");
    const pct = (amount: number) => (total > 0 ? (amount / total) * 100 : 0);
    const factor = formatNumber(this.selectedSupportFactor());
    const discount = this.state.aeu_discount_percent;
    const size = result && result.deployment_size;
    return (
      <Summary>
        <TotalCard>
          <TotalLabel>
            Estimated cost · {periodLabel(this.state.hours)} {calculating && <Loader active inline size="mini" inverted style={{ marginLeft: 8 }} />}
          </TotalLabel>
          {result ? (
            <>
              <TotalValue>${formatNumber(total)}</TotalValue>
              <TotalSub>{formatNumber(moneyAsNumber(result.total_credits))} credits</TotalSub>
              {listResult && listTotal > total && <TotalSub>List price ${formatNumber(listTotal)}</TotalSub>}
              {!_.isEmpty(revenueKinds) && (
                <>
                  <SplitBar>
                    <div style={{ width: `${pct(aeu)}%`, background: revenueColor }} />
                    <div style={{ width: `${pct(ddc)}%`, background: ddcColor }} />
                    <div style={{ width: `${pct(infra)}%`, background: infraColor }} />
                  </SplitBar>
                  <SplitRow>
                    <span>
                      <i style={{ background: revenueColor }} />
                      AEU
                    </span>
                    <span>${formatNumber(listAeu)}</span>
                  </SplitRow>
                  <SplitNote>{this.partNote("aeu")}</SplitNote>
                  {discountAmount > 0 && (
                    <>
                      <SplitSubRow>
                        <span>AEU discount {formatNumber(discount)}%</span>
                        <span>−${formatNumber(discountAmount)}</span>
                      </SplitSubRow>
                      <SplitSubRow>
                        <span>AEU after discount</span>
                        <span>${formatNumber(aeu)}</span>
                      </SplitSubRow>
                    </>
                  )}
                  {ddc > 0 && (
                    <>
                      <SplitRow>
                        <span>
                          <i style={{ background: ddcColor }} />
                          Dedicated DC
                        </span>
                        <span>${formatNumber(ddc)}</span>
                      </SplitRow>
                      <SplitNote>{this.partNote("ddc")}</SplitNote>
                    </>
                  )}
                  <SplitRow>
                    <span>
                      <i style={{ background: infraColor }} />
                      Infrastructure
                    </span>
                    <span>${formatNumber(infra)}</span>
                  </SplitRow>
                  <SplitNote>{this.partNote("infra")}</SplitNote>
                  <Formula>
                    AEU{discount > 0 && <> × (1 − {formatNumber(discount)}%)</>} × {factor}
                    {ddc > 0 && <> + Dedicated DC × {factor}</>} + Infrastructure
                  </Formula>
                </>
              )}
            </>
          ) : (
            <TotalValue>—</TotalValue>
          )}
        </TotalCard>
        <Card>
          <CardTitle>
            <Icon name="list" /> Breakdown
          </CardTitle>
          {this.state.errorMessage && <Message error size="tiny" content={this.state.errorMessage} />}
          {!result && !this.state.errorMessage && <Placeholder>{missing || "Calculating..."}</Placeholder>}
          {result && (
            <>
              {_.map(this.breakdownGroups(), (group) => (
                <BreakdownGroup key={group.name}>
                  {group.name && (
                    <GroupHeader color={group.color}>
                      <span>
                        {group.name}
                        {group.tag && <GroupTag>{group.tag}</GroupTag>}
                      </span>
                      <span>${formatNumber(group.amount)}</span>
                    </GroupHeader>
                  )}
                  {_.map(group.items, (item, index) => {
                    const amount = moneyAsNumber(item.amount);
                    const share = total > 0 && item.has_rate ? (amount / total) * 100 : 0;
                    return (
                      <ItemRow key={`item${index}`} muted={!item.has_rate}>
                        <ItemName title={itemLabel(item)}>{itemLabel(item)}</ItemName>
                        <ItemAmount>{item.has_rate ? `$${formatNumber(amount)}` : <NoRate>no rate</NoRate>}</ItemAmount>
                        <ItemQuantity>
                          {formatQuantity(item.quantity)} {item.unit}
                        </ItemQuantity>
                        <ItemShare>{item.has_rate ? `${formatNumber(share, 1)}%` : ""}</ItemShare>
                        <ShareBar>
                          <div style={{ width: `${share}%`, background: group.color }} />
                        </ShareBar>
                      </ItemRow>
                    );
                  })}
                </BreakdownGroup>
              ))}
              <Hint>Components without a rate in this plan version are listed but not counted in the total.</Hint>
              {size && (
                <SizeChips>
                  <span>
                    Agents {size.agents || 0} × {size.agent_memory_size || 0} GiB
                  </span>
                  <span>
                    Coordinators {size.coordinators || 0} × {size.coordinator_memory_size || 0} GiB
                  </span>
                  <span>
                    DB servers {size.dbservers || 0} × {size.dbserver_memory_size || 0} GiB / {size.dbserver_disk_size || 0} GiB
                  </span>
                  <span>
                    Gateways {size.gateways || 0} × {size.gateway_memory_size || 0} GiB
                  </span>
                </SizeChips>
              )}
            </>
          )}
        </Card>
      </Summary>
    );
  }

  render() {
    const plan = this.state.plan;
    return (
      <ContentSegment>
        <SecondaryMenu>
          <MenuActionBack />
          <Menu.Item header>Credit calculator{plan ? ` · ${plan.name || plan.id}` : ""}</Menu.Item>
          <LoaderBox>
            <Loader size="mini" active={this.props.loading} inline />
          </LoaderBox>
          <Menu.Item position="right">
            <Button size="tiny" basic onClick={() => this.props.history.push(Routes.dashboard_sales_creditPlan_detailsWithId(this.props.planId))}>
              Plan details
            </Button>
          </Menu.Item>
        </SecondaryMenu>
        <ErrorMessage active={!!this.props.error} onDismiss={this.props.clearError} message={this.props.error} />
        {plan && (
          <Page>
            <Column>
              {this.renderDeployment()}
              {this.renderSupport()}
              {this.renderUsage()}
            </Column>
            {this.renderSummary()}
          </Page>
        )}
      </ContentSegment>
    );
  }
}

export default withRefresh()(CreditPlanCalculator);
